import type { TyreLayoutSubjectKind } from '@chukua/common';
import { Alert } from 'antd';
import { CircleOff } from 'lucide-react';
import { Link } from 'react-router';
import { Breadcrumbs } from '../../../../../shared/components/Breadcrumbs';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import type { Crumb } from '../../../../../shared/components/section-trail';
import { REFERENCE_DATA_TAB_LABELS, referenceDataTabPath } from '../../../reference-data.paths';
import { VEHICLE_CLASS_TABS } from '../tyre-layouts.paths';

/** The vehicle takes the width; the details and axles keep a fixed column beside it, as the zone panel does beside the map. */
export const VEHICLE_CLASS_GRID = 'grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px]';

export const SUBJECT_NOUN: Record<TyreLayoutSubjectKind, string> = { truck_class: 'truck class', trailer_type: 'trailer type' };

/** The title is the row's name, so the whole header waits with the data; the breadcrumbs are known at once. */
export function VehicleClassPageSkeleton({ trail, kind }: { trail: Crumb[]; kind: TyreLayoutSubjectKind }) {
  return (
    <SkeletonRegion label={`Loading ${SUBJECT_NOUN[kind]}`}>
      <div className="mb-8 flex flex-col gap-2.5">
        <Breadcrumbs trail={trail} />
        <Skeleton className="h-7 w-56 max-w-full" />
        <Skeleton className="h-4 w-72 max-w-full" />
      </div>
      <div className={VEHICLE_CLASS_GRID}>
        <Skeleton className="h-96 w-full rounded-lg" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-56 w-full rounded-lg" />
        </div>
      </div>
    </SkeletonRegion>
  );
}

export function VehicleClassMissing({ trail, kind, code }: { trail: Crumb[]; kind: TyreLayoutSubjectKind; code: string }) {
  const tab = VEHICLE_CLASS_TABS[kind];
  const noun = SUBJECT_NOUN[kind];
  return (
    <>
      <PageHeader title={noun.charAt(0).toUpperCase() + noun.slice(1)} breadcrumbs={trail} />
      <EmptyState icon={CircleOff} title={`No ${noun} with code ${code}`} description="It may have been renamed. Pick it again from the list.">
        <Link to={referenceDataTabPath(tab)}>Back to {REFERENCE_DATA_TAB_LABELS[tab].toLowerCase()}</Link>
      </EmptyState>
    </>
  );
}

export function VehicleClassLoadError({ trail, kind }: { trail: Crumb[]; kind: TyreLayoutSubjectKind }) {
  const noun = SUBJECT_NOUN[kind];
  return (
    <>
      <PageHeader title={noun.charAt(0).toUpperCase() + noun.slice(1)} breadcrumbs={trail} />
      <Alert type="error" showIcon title={`Could not load the ${noun}.`} />
    </>
  );
}
